import { Alert, Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/system';
import dayjs from 'dayjs';
import { Point } from 'highcharts';
import { every, get, isEmpty, map, omit } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';
import WhiteBoxContent from 'src/components/common/WhiteBoxContent';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import ModalComponent from 'src/components/Modal/ModalComponent';
import Table from 'src/components/Table';
import FilterChart from 'src/features/TradeData/Common/FilterChart';
import { TypeTabTradeData } from 'src/features/TradeData/config';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryGetBuyerSupplierCountryCompany,
  useQueryGetBuyerSupplierCountryTable,
  useQueryGetBuyerSupplierCountryWorldMap,
  useQueryGetSupplierCountryAIExplanation,
} from 'src/services/queries/useQueryTradeData';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import {
  checkCompanyLogistics,
  getCountryByCountryCode,
  renderNumberOrDefault,
  renderTextOrDefault,
  renderValueNumberTrade,
} from 'src/utils/helpers/stringHelpers';
import {
  IGetSupplierBuyerCountryRequestPayload,
  IGetTradeDataCompanyDetailTable,
  IGetTradeDataSupplierBuyerCountry,
  ISearchGlobalTradeDataQueryParams,
} from 'src/utils/typings/dto/tradeData';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

import { AutoAwesome } from '@mui/icons-material';
import { AlertCircle, Award, TrendingUp, Users } from 'react-feather';
import AISummaryLoading from 'src/components/common/AISummaryLoading';
import IconFeather from 'src/components/common/Icon';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipLogisticCompany from 'src/components/common/TooltipLogisticCompany';
import TooltipTradeDataLimit from 'src/components/common/TooltipTradeDataLimit';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import CompanyInfoNoData from 'src/features/CompanyDetail/Common/CompanyInfoNoData';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import { LIST_RESTRICTION_DATA, useProvisionRestriction } from 'src/hooks/useProvisionRestriction';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';

const SupplierBuyerCountry = ({ selectedTab }: { selectedTab: TypeTabTradeData }) => {
  const {
    palette: { text, background, primary },
    shape: { borderRadius },
  } = useTheme();

  const { hs_code, start_date, end_date, keyword } = useQueryParams();
  const { checkRestriction } = useProvisionRestriction();

  const { t } = useTranslation();
  const queryParams = useQueryParams() as ISearchGlobalTradeDataQueryParams;
  const isBuyer = selectedTab === TypeTabTradeData.BuyerCountry;
  const [selectedRecord, setSelectedRecord] = useState<ISupplierBuyerCountryTableItem | undefined>(undefined);
  const { setSort: setTableSort, supplierBuyerCountrySortParams } = useTradeDataSortStore();
  const { setSort: setPopupTableSort, supplierBuyerCountryPopupTableSortParams } = useTradeDataSortStore();

  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const [supplierBuyerCountryTablePaging, setSupplierBuyerCountryTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  const [supplierBuyerCountryCompanyTablePaging, setSupplierBuyerCountryCompanyTablePaging] = useState({
    page: 0,
    page_size: 10,
  });

  useEffect(() => {
    setSupplierBuyerCountryTablePaging({
      page: 0,
      page_size: 10,
    });
  }, [selectedTab]);

  const supplierBuyerCountryTableRequestPayload = useMemo<IGetSupplierBuyerCountryRequestPayload>(() => {
    const sortField = supplierBuyerCountrySortParams?.sortBy;

    return {
      ...queryParams,
      limit: supplierBuyerCountryTablePaging.page_size,
      offset: supplierBuyerCountryTablePaging.page,
      start_date: selectedRangeTime.fromTime || start_date,
      end_date: selectedRangeTime.toTime || end_date,
      hs_code: hs_code,
      is_buyer: isBuyer,
      sort_by: sortField,
      order: supplierBuyerCountrySortParams?.sort,
    };
  }, [
    selectedRangeTime,
    hs_code,
    isBuyer,
    start_date,
    end_date,
    supplierBuyerCountryTablePaging,
    supplierBuyerCountrySortParams,
  ]);
  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const [isModalBuySupplyDetailListOpen, setIsModalBuySupplyDetailListOpen] = useState(false);

  const [detailTableViewType, setDetailTableViewType] = useState<TypeBuyerSupplier>();

  const { data, dataMapping, isFetching } = useQueryGetBuyerSupplierCountryTable(
    acceptCreditPayment ? supplierBuyerCountryTableRequestPayload : undefined,
  );

  useEffect(() => {
    if (!isModalBuySupplyDetailListOpen) {
      setSupplierBuyerCountryCompanyTablePaging({
        page: 0,
        page_size: 10,
      });
    }
  }, [isModalBuySupplyDetailListOpen]);

  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(tradeDataUnitByFilter.value);

  const unitFieldMapping = {
    bol: {
      field: 'number_of_bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value', label: 'USD' },
  };

  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;

  const supplierBuyerCountryRequestPayload: IGetTradeDataSupplierBuyerCountry = useMemo(() => {
    return {
      ...omit(queryParams, []),
      start_date: selectedRangeTime.fromTime,
      end_date: selectedRangeTime.toTime,
      is_buyer: isBuyer,
    };
  }, [queryParams, selectedRangeTime, isBuyer]);

  const [supplierBuyerCountryCompanyRequestPayload, setSupplierBuyerCountryCompanyRequestPayload] =
    useState<IGetTradeDataCompanyDetailTable>();

  const supplierBuyerCountryWorldMapResp = useQueryGetBuyerSupplierCountryWorldMap(
    acceptCreditPayment ? supplierBuyerCountryRequestPayload : undefined,
  );

  // Waiting API get list finished
  const supplierBuyerCountryAIResp = useQueryGetSupplierCountryAIExplanation(
    !supplierBuyerCountryWorldMapResp?.isFetching ? supplierBuyerCountryRequestPayload : undefined,
  );
  const supplierBuyerCountryCompanyResp = useQueryGetBuyerSupplierCountryCompany(
    acceptCreditPayment ? supplierBuyerCountryCompanyRequestPayload : undefined,
  );

  // const transactionsPaymentStatusResp = useQueryTransactionsPaymentStatus(supplierBuyerCountryRequestPayload);

  const [shouldShowBillableDataLoading] = useCreditFlow({
    response: data,
    featureInfo: {
      featureId: LIST_FEATURES.TRADE_DATA_BILL_OF_LADING,
      action: LIST_ACTIONS.VIEW,
    },
    // paymentStatus: transactionsPaymentStatusResp.data?.data?.bol,
    // isCollectingData: transactionsPaymentStatusResp.isFetching,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const [shouldShowLoadingPopup] = useCreditFlow({
    response: supplierBuyerCountryCompanyResp?.data,
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
    cbClose: () => setIsModalBuySupplyDetailListOpen(false),
  });

  const supplierBuyerCountryCompany = getArrayDefault(supplierBuyerCountryCompanyResp.data?.data);
  const supplierBuyerCountryAIExplanation = supplierBuyerCountryAIResp.data?.data?.ai_report;

  const supplierBuyerCountryWorldMap = useMemo(() => {
    return supplierBuyerCountryWorldMapResp?.dataMapping?.map((item: IMarketAnalyticsWorldMap) => {
      return [
        item.country_code?.toLowerCase(),
        item?.[chartFieldNameByUnitFilter as keyof IMarketAnalyticsWorldMap],
      ] as [string, number];
    });
  }, [supplierBuyerCountryWorldMapResp?.dataMapping, chartFieldNameByUnitFilter]);

  useEffect(() => {
    setSupplierBuyerCountryCompanyRequestPayload((value) => {
      if (!value) return value;
      return {
        ...value,
        offset: supplierBuyerCountryCompanyTablePaging.page,
        limit: supplierBuyerCountryCompanyTablePaging.page_size,
        sort_by: supplierBuyerCountryPopupTableSortParams?.sortBy,
        order: supplierBuyerCountryPopupTableSortParams?.sort,
      };
    });
  }, [supplierBuyerCountryCompanyTablePaging, supplierBuyerCountryPopupTableSortParams]);

  const configColumn: ConfigCell<ISupplierBuyerCountryTableItem>[] = [
    {
      disablePadding: false,
      id: 'port_name',
      label: t('trade_data.supplier_buyer_country.table.country'),
      disableSort: true,
      cell: (data: ISupplierBuyerCountryTableItem) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <Typography variant="body2" color="text.secondary">
              {renderTextOrDefault(getCountryByCountryCode(data?.country_code).label)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ISupplierBuyerCountryTableItem) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.shipments)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ISupplierBuyerCountryTableItem) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ISupplierBuyerCountryTableItem) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.teu)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      width: 200,
      cell: (data: ISupplierBuyerCountryTableItem) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {formatValueUSD(data.value_usd)}
          </Typography>
        );
      },
    },
    {
      disablePadding: false,
      id: 'company',
      label: isBuyer
        ? t('trade_data.supplier_buyer_country.table.total_buyers')
        : t('trade_data.supplier_buyer_country.table.total_supplier'),
      width: 150,
      cell: (data: ISupplierBuyerCountryTableItem) => {
        const dateRange = [
          dayjs().startOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
          dayjs().endOf('month').format(FORMAT_DATE_TIME.yyyy_mm_dd),
        ];
        return (
          <Typography
            variant="body2"
            color="secondary"
            onClick={() => {
              setSelectedRecord(data);
              // setOpenBuyerList(true);

              setSupplierBuyerCountryCompanyRequestPayload({
                keyword,
                offset: supplierBuyerCountryCompanyTablePaging.page,
                limit: supplierBuyerCountryCompanyTablePaging.page_size,
                is_buyer: isBuyer,
                hs_code: hs_code,
                start_date: selectedRangeTime.fromTime || start_date,
                end_date: selectedRangeTime.toTime || end_date,
                exp_country_code: (!isBuyer ? data.country_code : undefined) || queryParams.exp_country_code,
                imp_country_code: (isBuyer ? data.country_code : undefined) || queryParams.imp_country_code,
                supplier_id: queryParams.supplier_id,
                buyer_id: queryParams.buyer_id,
                sort_by: supplierBuyerCountryPopupTableSortParams?.sortBy,
                order: supplierBuyerCountryPopupTableSortParams?.sort,
              });
              setDetailTableViewType(isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier);
              setIsModalBuySupplyDetailListOpen(true);
            }}
          >
            {renderNumberOrDefault(isBuyer ? data.buyers : data.suppliers)}
          </Typography>
        );
      },
    },
  ];

  const configBuySupplyDetailListColumns: ConfigCell<IMarketAnalyticsCompany>[] = [
    {
      disablePadding: false,
      id: 'company_name',
      label: t('market_analytics.table.column_name.company_name'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Stack direction="row" spacing={1}>
          <FlagCountry code={data?.company.country_code || ''} />
          <LinkToCompanyDetail
            id={data.company.id}
            companyName={data.company_name || ''}
            params={queryParams}
            viewedAsType={isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
          />
          {checkCompanyLogistics(data?.company_name || '') && <TooltipLogisticCompany />}
        </Stack>
      ),
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('market_analytics.table.column_name.no_of_bl'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('market_analytics.table.column_name.weight'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('market_analytics.table.column_name.teu'),
      cell: (data: IMarketAnalyticsCompany) => (
        <Typography variant="body2" color={'text.secondary'}>
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: IMarketAnalyticsCompany) => {
        const checkRestrictionValue = checkRestriction({
          countryCode: data?.company?.country_code || '',
          typeData: LIST_RESTRICTION_DATA.VALUES_USD,
        });
        return (
          <Typography variant="body2" color={'text.secondary'}>
            <Typography variant="body2" color="text.secondary">
              {formatValueUSD(data.value_usd)}
            </Typography>
            {checkRestrictionValue && <TooltipTradeDataLimit />}
          </Typography>
        );
      },
    },
  ];

  const renderTitle = () => {
    switch (detailTableViewType) {
      case TypeBuyerSupplier.buyer:
        return t('market_analytics.table.column_name.number_of_buyers');
      case TypeBuyerSupplier.supplier:
        return t('market_analytics.table.column_name.number_of_suppliers');
    }
  };

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (isFetching || tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy)) return;
    const isEmptyData = isEmpty(dataMapping) || every(dataMapping, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [data, isFetching]);

  if (
    !isFetching &&
    !supplierBuyerCountryWorldMapResp.isFetching &&
    isEmpty(getArrayDefault(dataMapping)) &&
    isEmpty(getArrayDefault(supplierBuyerCountryWorldMap))
  ) {
    return (
      <CompanyInfoNoData
        title={t('trade_data.common.notice_no_data.title')}
        content={<>{t('trade_data.common.notice_no_data.sub_title')}</>}
      />
    );
  }

  return (
    <Stack spacing={3}>
      <WhiteBoxContent display={'flex'} flexDirection={'column'} gap={3}>
        <FilterChart
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={
            isBuyer
              ? t('trade_data.supplier_buyer_country.which_country_is_importing')
              : t('trade_data.supplier_buyer_country.which_country_is_exporting')
          }
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
            setTableSort('supplierBuyerCountrySortParams', {
              sortBy: unit,
              sort: 'desc',
            });
          }}
          selectedUnitBy={unitFilter}
          selectedRangeTime={selectedRangeTime}
        />
        <WorldMapChart
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(point.properties['hc-key']);
            return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${text.secondary}">${chartLabelByUnitFilter} </b> <b>${renderNumberOrDefault(point.value)}</b>
          </div>`;
          }}
          data={supplierBuyerCountryWorldMap}
          isLoading={supplierBuyerCountryWorldMapResp.isFetching}
          height={580}
          type={WorldMapOptions.customer}
        />
      </WhiteBoxContent>
      {/* Show screen "Analyzing data" while  get list done and fetching ai_content */}
      {supplierBuyerCountryAIResp?.isFetching && supplierBuyerCountryWorldMapResp?.isFetched && <AISummaryLoading />}
      {/* Check if all values on AI content is empty => HIDE */}
      {!supplierBuyerCountryAIResp.isFetching &&
        every(Object.values(supplierBuyerCountryAIExplanation), (content) => !isEmpty(content)) && (
          <WhiteBoxContent
            sx={{
              background: `linear-gradient(90deg, ${alpha(primary.light, 0.2)} 0%,${alpha(primary.light, 0.05)} 100%)`,
            }}
            border={`1px solid ${primary.light}`}
          >
            <Stack alignItems="center" direction="row" gap={3} mb={4}>
              <AutoAwesome color="primary" />
              <Typography color="text.primary" variant="h5">
                AI Summary
              </Typography>
            </Stack>
            <Stack direction="column" gap={2}>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<TrendingUp />} muiProps={{ color: 'primary' }} />
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary.dark">
                    Top {isBuyer ? 'Buyers' : 'Suppliers'}
                  </Typography>
                  <Stack direction="column" gap={2} mt={2}>
                    {map(
                      isBuyer
                        ? supplierBuyerCountryAIExplanation?.top_importers
                        : supplierBuyerCountryAIExplanation?.top_exporters,
                      (country) => (
                        <WhiteBoxContent
                          p={2}
                          bgcolor={'color-mix(in oklab, #fff 50%, transparent)'}
                          border={`1px solid ${alpha(primary.light, 0.5)}`}
                        >
                          <Typography
                            color="white"
                            bgcolor={primary.main}
                            borderRadius={borderRadius / 2}
                            display={'inline-block'}
                            paddingY={0.5}
                            paddingX={1}
                          >
                            {getCountryByCountryCode(country?.country_code || '')?.label || country?.country_code}
                          </Typography>
                          <Typography color="text.secondary">{country?.insight}</Typography>
                        </WhiteBoxContent>
                      ),
                    )}
                  </Stack>

                  <Typography variant="body1">{supplierBuyerCountryAIExplanation.trend}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<AlertCircle />} muiProps={{ color: 'warning' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="warning.dark">
                    Anomaly
                  </Typography>
                  <Typography variant="body1">{supplierBuyerCountryAIExplanation.anomaly}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<Users />} muiProps={{ color: 'info' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="info.dark">
                    {isBuyer ? 'Buyers' : 'Suppliers'} Structure
                  </Typography>
                  <Typography variant="body1">
                    {isBuyer
                      ? supplierBuyerCountryAIExplanation?.buyer_structure
                      : supplierBuyerCountryAIExplanation?.supplier_structure}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" gap={2}>
                <IconFeather icon={<Award />} muiProps={{ color: 'success' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="success.dark">
                    Opportunity
                  </Typography>
                  <Typography variant="body1">{supplierBuyerCountryAIExplanation.opportunity_insight}</Typography>
                </Box>
              </Stack>
            </Stack>
          </WhiteBoxContent>
        )}
      <WhiteBoxContent>
        <Stack direction="column" spacing={3}>
          <Typography display={'flex'} justifyContent={'space-between'} textAlign="left" variant="h5">
            {isBuyer
              ? t('trade_data.supplier_buyer_country.buyer_country_data')
              : t('trade_data.supplier_buyer_country.supplier_country_data')}

            <ExportDataTable
              fileName={isBuyer ? 'buyer-country' : 'supplier-country'}
              paramsExport={supplierBuyerCountryTableRequestPayload}
              featureId={isBuyer ? LIST_FEATURES.TRADE_DATA_BUYER_COUNTRY : LIST_FEATURES.TRADE_DATA_SUPPLIER_COUNTRY}
              endpoint={
                isBuyer
                  ? get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_BUYER_COUNTRY)
                  : get(URL_EXPORT_MAPPING, LIST_FEATURES.TRADE_DATA_SUPPLIER_COUNTRY)
              }
              totalRecordsAvailable={data?.total_records || 0}
            />
          </Typography>
          <Table
            handleSort={(data) => {
              setTableSort('supplierBuyerCountrySortParams', data);
            }}
            tableMaxHeight={600}
            onChangePage={(value) => {
              setSupplierBuyerCountryTablePaging({ ...supplierBuyerCountryTablePaging, page: value });
            }}
            onChangePageSize={(value) => setSupplierBuyerCountryTablePaging({ page: 0, page_size: value })}
            loading={shouldShowBillableDataLoading || isFetching}
            page={data.page}
            pageSize={data.page_size}
            totalRecords={data.total_records}
            dataKey={'id'}
            data={dataMapping}
            configColumn={configColumn}
          />
        </Stack>
      </WhiteBoxContent>
      <ModalComponent
        padding={0}
        pt={0}
        handleClose={() => setIsModalBuySupplyDetailListOpen(false)}
        open={isModalBuySupplyDetailListOpen}
      >
        <Stack direction="column" spacing={3} width="80vw" bgcolor={background.paper} p={4}>
          <Stack alignItems={'center'} direction="row" justifyContent="space-between">
            <Typography variant="h5">{renderTitle()}</Typography>
            {hs_code ? (
              <Typography variant="body2" color="text.secondary">
                This is a list of {isBuyer ? 'buyers' : 'suppliers'} with HS code {hs_code} from{' '}
                {dayjs(start_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)} until{' '}
                {dayjs(end_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This is a list of {isBuyer ? 'buyers' : 'suppliers'} with production description <b>{keyword}</b> from{' '}
                {dayjs(start_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)} until{' '}
                {dayjs(end_date).format(FORMAT_DATE_TIME.LONG_MONTH_DAY_YEAR)}
              </Typography>
            )}
            <ExportDataTable
              fileName={isBuyer ? 'buyers-country-buyer-list' : 'suppliers-country-supplier-list'}
              paramsExport={supplierBuyerCountryCompanyRequestPayload}
              featureId={LIST_FEATURES.INTERNAL_TRADE_DATA_SUB_LIST_SUPPLIERS_BUYERS}
              endpoint={
                isBuyer
                  ? get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_BUYERS_COUNTRY_BUYER_LIST)
                  : get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.TRADE_DATA_SUPPLIERS_COUNTRY_SUPPLIER_LIST)
              }
              totalRecordsAvailable={supplierBuyerCountryCompanyResp?.data?.total_records || 0}
            />
          </Stack>

          {isEmpty(supplierBuyerCountryCompany) && !supplierBuyerCountryCompanyResp.isFetching ? (
            <Alert severity="info" color="info">
              Withheld by Government
            </Alert>
          ) : (
            <Table
              maxRow={10}
              onChangePage={(value) =>
                setSupplierBuyerCountryCompanyTablePaging({ ...supplierBuyerCountryCompanyTablePaging, page: value })
              }
              handleSort={(data) => {
                setPopupTableSort('supplierBuyerCountryPopupTableSortParams', data);
              }}
              onChangePageSize={(value) => setSupplierBuyerCountryCompanyTablePaging({ page: 0, page_size: value })}
              loading={shouldShowLoadingPopup || supplierBuyerCountryCompanyResp.isFetching}
              page={supplierBuyerCountryCompanyTablePaging.page}
              pageSize={supplierBuyerCountryCompanyTablePaging.page_size}
              totalRecords={supplierBuyerCountryCompanyResp.data.total_records}
              dataKey={(item) => item.company?.id}
              data={supplierBuyerCountryCompany}
              configColumn={configBuySupplyDetailListColumns}
              pagingTooltipsText={
                ((isBuyer ? selectedRecord?.buyers : selectedRecord?.suppliers) || 0) >
                (supplierBuyerCountryCompanyResp?.data.total_records || 9999999)
                  ? 'trade_data.count_mismatch.tooltip'
                  : undefined
              }
            />
          )}
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default SupplierBuyerCountry;
